import type { ColumnDef } from '@tanstack/react-table'
import type { PathHelpers } from '@/hooks/usePathHelper'
import type {
  SalesTaxReportActivityKind,
  SalesTaxReportJurisdiction,
  SalesTaxReportRow,
} from '@/lib/services/reports/reports.types'
import { cn, formatCurrency } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { DataTableColumnHeader } from '@/components/tables/DataTableColumnHeader'
import { formatTransactionDate } from '../sales-statistics/-formatters'
import { formatJurisdictionRate, formatRateSummary } from './-presentation'

// Activity that adds sales reads as positive; refunds and cancellations remove them. An order change can go either way.
const activityBadgeVariants: Record<SalesTaxReportActivityKind, BadgeVariant> = {
  sale: 'success',
  order_change: 'secondary',
  refund: 'destructive',
  refund_reversal: 'success',
  cancellation: 'destructive',
  shop_fee: 'success',
  shop_fee_refund: 'destructive',
  shop_fee_refund_reversal: 'success',
  shop_fee_cancellation: 'destructive',
}

type DetailedLedgerColumnsOptions = {
  path: PathHelpers
  /** The `$orgId` route param, parsed to a number. */
  orgId: number
  /** The report's timezone (`filters.timezone`). */
  timezone: string | undefined
}

export function getDetailedLedgerColumns({
  path,
  orgId,
  timezone,
}: DetailedLedgerColumnsOptions): ColumnDef<SalesTaxReportRow>[] {
  return [
    {
      id: 'activity_label',
      accessorKey: 'activity_label',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Activity" />,
      cell: ({ row }) => (
        <Badge variant={activityBadgeVariants[row.original.activity_kind]}>
          {row.original.activity_label}
        </Badge>
      ),
      meta: {
        label: 'Activity',
        kind: 'badge',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3',
      },
    },
    {
      id: 'sales_order_id',
      accessorKey: 'sales_order_id',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Order" />,
      cell: ({ row }) =>
        row.original.sales_order_id ? (
          <Link {...path.salesOrders.show(row.original.sales_order_id).using({ orgId }).link}>
            <Typography variant="label" interaction="hoverUnderline">
              #{row.original.sales_order_id}
            </Typography>
          </Link>
        ) : (
          <Typography variant="supporting">—</Typography>
        ),
      meta: {
        label: 'Order',
        kind: 'id',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
      },
    },
    {
      id: 'jurisdiction_label',
      accessorKey: 'jurisdiction_label',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Jurisdiction" />,
      cell: ({ row }) => (
        <JurisdictionCell
          jurisdictionLabel={row.original.jurisdiction_label}
          detailLabel={row.original.jurisdiction_detail_label}
        />
      ),
      meta: {
        label: 'Jurisdiction',
        kind: 'primary',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium',
      },
    },
    {
      id: 'gross_taxable_sales',
      accessorKey: 'gross_taxable_sales',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Taxable Amount" />,
      cell: ({ row }) => formatCurrency(row.original.gross_taxable_sales),
      meta: {
        label: 'Taxable Amount',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
      },
    },
    {
      id: 'untaxable_amount',
      accessorKey: 'untaxable_amount',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Non-taxable Amount" />,
      cell: ({ row }) => formatCurrency(row.original.untaxable_amount),
      meta: {
        label: 'Non-taxable Amount',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
      },
    },
    {
      id: 'tax_rate',
      accessorKey: 'tax_rate',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Tax Rate" />,
      cell: ({ row }) => formatJurisdictionRate(row.original.tax_rate),
      meta: {
        label: 'Tax Rate',
        kind: 'number',
        align: 'end',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 tabular-nums',
      },
    },
    {
      id: 'tax_collected',
      accessorKey: 'tax_collected',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Tax Collected" />,
      cell: ({ row }) => formatCurrency(row.original.tax_collected),
      meta: {
        label: 'Tax Collected',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 font-medium tabular-nums',
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) =>
        row.original.created_at ? (
          formatTransactionDate(row.original.created_at, timezone)
        ) : (
          <Typography variant="supporting">—</Typography>
        ),
      meta: {
        label: 'Date',
        kind: 'date',
        headClassName: 'whitespace-nowrap px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3 tabular-nums',
      },
    },
  ]
}

type JurisdictionSummaryColumnsOptions = {
  chartRowColorByLabel: Map<string, string | undefined>
  onOpenJurisdiction: (jurisdiction: SalesTaxReportJurisdiction) => void
}

export function getJurisdictionSummaryColumns({
  chartRowColorByLabel,
  onOpenJurisdiction,
}: JurisdictionSummaryColumnsOptions): ColumnDef<SalesTaxReportJurisdiction>[] {
  return [
    {
      id: 'jurisdiction_label',
      accessorKey: 'jurisdiction_label',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Jurisdiction" />,
      enableSorting: false,
      cell: ({ row }) => (
        <JurisdictionCell
          jurisdictionLabel={row.original.jurisdiction_label}
          chartColor={chartRowColorByLabel.get(row.original.jurisdiction_label)}
          truncate
        />
      ),
      meta: {
        label: 'Jurisdiction',
        kind: 'primary',
        headClassName: 'whitespace-nowrap px-4 w-full lg:w-[40%]',
        cellClassName: 'whitespace-nowrap px-4 py-3',
      },
    },
    {
      id: 'gross_taxable_sales',
      accessorKey: 'gross_taxable_sales',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Taxable Amount" />,
      enableSorting: false,
      cell: ({ row }) => formatCurrency(row.original.gross_taxable_sales),
      meta: {
        label: 'Taxable Amount',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap pl-8 pr-5',
        cellClassName: 'whitespace-nowrap py-3 pl-8 pr-5 tabular-nums',
      },
    },
    {
      id: 'untaxable_amount',
      accessorKey: 'untaxable_amount',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Non-taxable Amount" />,
      enableSorting: false,
      cell: ({ row }) => formatCurrency(row.original.untaxable_amount),
      meta: {
        label: 'Non-taxable Amount',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap pl-7 pr-5',
        cellClassName: 'whitespace-nowrap py-3 pl-7 pr-5 tabular-nums',
      },
    },
    {
      id: 'tax_rates',
      accessorKey: 'tax_rates',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Tax Rate" />,
      enableSorting: false,
      cell: ({ row }) => formatRateSummary(row.original.tax_rates),
      meta: {
        label: 'Tax Rate',
        kind: 'number',
        align: 'end',
        headClassName: 'whitespace-nowrap pl-7 pr-5',
        cellClassName: 'whitespace-nowrap py-3 pl-7 pr-5 tabular-nums',
      },
    },
    {
      id: 'tax_collected',
      accessorKey: 'tax_collected',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Tax Collected" />,
      enableSorting: false,
      cell: ({ row }) => formatCurrency(row.original.tax_collected),
      meta: {
        label: 'Tax Collected',
        kind: 'money',
        align: 'end',
        headClassName: 'whitespace-nowrap pl-7 pr-5',
        cellClassName: 'whitespace-nowrap py-3 pl-7 pr-5 font-medium tabular-nums',
      },
    },
    {
      id: 'district_details',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Special Districts" />,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.has_multiple_special_districts ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenJurisdiction(row.original)}
            className="whitespace-nowrap"
          >
            View districts
          </Button>
        ) : (
          <Typography variant="supporting">—</Typography>
        ),
      meta: {
        label: 'Special Districts',
        kind: 'actions',
        link: false,
        headClassName: 'w-[1%] px-4',
        cellClassName: 'whitespace-nowrap px-4 py-3',
      },
    },
  ]
}

type JurisdictionCellProps = {
  jurisdictionLabel: string
  detailLabel?: string | null
  chartColor?: string
  truncate?: boolean
}

function JurisdictionCell({
  jurisdictionLabel,
  detailLabel = null,
  chartColor,
  truncate = false,
}: JurisdictionCellProps) {
  return (
    <div className="flex min-w-0 items-center gap-3 py-1 font-medium">
      {chartColor ? (
        <span
          aria-hidden="true"
          data-testid="jurisdiction-color-dot"
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: chartColor }}
        />
      ) : null}
      <div className="min-w-0">
        <span className={cn('block', truncate && 'truncate')} title={jurisdictionLabel}>
          {jurisdictionLabel}
        </span>
        {detailLabel ? (
          <Typography variant="caption" className="block">
            {detailLabel}
          </Typography>
        ) : null}
      </div>
    </div>
  )
}
